import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import LandingPage from "@/pages/LandingPage";
import AnalysisForm from "@/pages/AnalysisForm";
import AnalysisResult from "@/pages/AnalysisResult";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import PricingPage from "@/pages/PricingPage";
import InvestmentsPage from "@/pages/InvestmentsPage";
import SimulatorPage from "@/pages/SimulatorPage";
import ExpensesPage from "@/pages/ExpensesPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PixPayment from "@/pages/PixPayment";
import AdminSuggestions from "@/pages/AdminSuggestions";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import UpdateNotification from "@/components/UpdateNotification";
import BottomNavBar from "@/components/BottomNavBar";
import GuidedTour from "@/components/GuidedTour";
import RetentionBanners from "@/components/RetentionBanners";
import PlanExpiredBlock from "@/components/PlanExpiredBlock";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TourProvider } from "@/context/TourContext";
import { usePageTracking } from "@/hooks/useAnalytics";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Page tracking component
const PageTracker = () => {
  usePageTracking();
  return null;
};

// Service Worker notification click handler
const NotificationHandler = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        const url = event.data.url;
        if (url) {
          // Navigate to the URL from the notification
          navigate(url);
        }
      }
    };

    // Listen for messages from Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [navigate]);

  return null;
};

function AppRouter() {
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  
  // If there's ANY auth-related parameter (session_id, error, etc.), 
  // let AuthCallback handle it - NEVER silently swallow errors
  if (hash.includes('session_id=') || search.includes('session_id=') ||
      hash.includes('error=') || search.includes('error=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <PageTracker />
      <NotificationHandler />
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/analyze" element={<AnalysisForm />} />
      <Route path="/result/:analysisId" element={<AnalysisResult />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/investments" element={<InvestmentsPage />} />
      <Route path="/simulator" element={<SimulatorPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/pix" element={<PixPayment />} />
      <Route path="/expenses" element={<ExpensesPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/nicolas" 
        element={
          <ProtectedRoute>
            <AdminSuggestions />
          </ProtectedRoute>
        } 
      />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <TourProvider>
            <div className="App min-h-screen bg-[#050505] pb-16">
              <RetentionBanners />
              <PlanExpiredBlock />
              <AppRouter />
              <BottomNavBar />
              <GuidedTour />
              <UpdateNotification />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#0A0A0A',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            />
          </div>
          </TourProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
